import { useCallback, useEffect, useMemo } from 'react'

import {
  Icon,
  IconButton,
  Instruction,
  Text,
  BottomSheet,
  Loader,
} from 'components'

import { EmptyState, Note, RegisteredPayment } from './components'

import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import {
  AccountPaymentMethod,
  PatrimonyPaymentMethod,
} from 'services/patrimony/types'

import { useTenant } from 'shared/hooks'
import { useForm } from 'react-hook-form'

import { useGetPaymentMethod } from 'services/patrimony'

import styles from './styles.module.scss'

type Form = {
  patrimony: PatrimonyPaymentMethod
  account: AccountPaymentMethod
}

const Method = () => {
  const { tenant } = useTenant()
  const { userId } = useParams()

  const navigate = useNavigate()

  const [searchParams, setSearchParams] = useSearchParams()

  const { setValue, watch, register } = useForm<Form>({ mode: 'onChange' })

  const selectedPatrimony = watch('patrimony')
  const selectedAccount = watch('account')

  const {
    data: patrimonies,
    isFetching,
    isError,
  } = useGetPaymentMethod(userId || '')

  const pendingAccounts = useMemo(
    () =>
      patrimonies?.filter((patrimony) =>
        patrimony.accounts.some((account) => !account.paymentMethod),
      ),
    [patrimonies],
  )

  const onlyOneAccount = selectedPatrimony?.accounts?.length === 1

  const handleDefaultValues = useCallback(() => {
    const defaultPatrimony = patrimonies?.find((patrimony) => {
      const patrimonyId = searchParams.get('patrimonyId')

      if (patrimonyId) return patrimony.id === patrimonyId

      return patrimony.accounts[0]
    })

    if (defaultPatrimony) {
      setValue('patrimony', defaultPatrimony)

      const defaultAccount = defaultPatrimony.accounts.find((account) => {
        const accountId = searchParams.get('accountId')

        if (accountId) return account.id === accountId

        return account
      })

      if (defaultAccount) setValue('account', defaultAccount)

      setSearchParams({
        patrimonyId: defaultPatrimony.id,
        ...(!!defaultAccount && {
          accountId: defaultAccount?.id,
        }),
      })
    }
  }, [patrimonies])

  const handleRender = () => {
    if (isError) return 'error'

    if (isFetching) return 'loading'

    return 'view'
  }

  useEffect(() => {
    handleDefaultValues()
  }, [patrimonies])

  return (
    <>
      {
        {
          view: !!patrimonies?.length && (
            <div className={styles.container}>
              <header className={styles.header}>
                <IconButton
                  className={styles.navigation}
                  onClick={() =>
                    (window.location.href = 'br.com.orsegups.one://')
                  }
                >
                  <Icon
                    width={20}
                    name="chevron-sm-left"
                    color="element-default"
                  />
                </IconButton>

                {/*TODO: remover responsabilidade da tela quando aplicado novo sistema de tenant */}
                <Icon name={`${tenant}-logo`} width={151} height={28} />
              </header>

              <div className={styles.wrapper}>
                <div className={styles.formContent}>
                  <Text weight="semibold" size="3xl" color="element-bold">
                    Conta
                  </Text>

                  {!!pendingAccounts?.length && (
                    <Note
                      message="Há contas em seus patrimônios sem forma de pagamento definida. Cadastre uma agora para garantir o funcionamento do seu serviço."
                      onAction={() => {
                        if (pendingAccounts.length > 1) {
                          navigate(`/pending-accounts/${userId}`)
                        } else {
                          setValue('patrimony', pendingAccounts[0])
                          setValue('account', pendingAccounts[0].accounts[0])
                        }
                      }}
                    />
                  )}

                  <div>
                    <Text weight="medium" size="lg">
                      Selecione patrimônio e conta
                    </Text>

                    <div className={styles.optionContainer}>
                      <BottomSheet.Root>
                        <BottomSheet.Trigger>
                          <div className={styles.selected}>
                            <div>
                              <Text
                                size="sm"
                                weight="medium"
                                color="element-medium"
                                as="label"
                              >
                                Patrimônio
                              </Text>
                              <Text
                                size="md"
                                weight="regular"
                                color="element-default"
                                {...register('patrimony')}
                              >
                                {selectedPatrimony?.name}
                              </Text>
                            </div>

                            <IconButton>
                              <Icon
                                width={17}
                                name="edit"
                                color="element-default"
                              />
                            </IconButton>
                          </div>
                        </BottomSheet.Trigger>

                        <BottomSheet.Content>
                          <BottomSheet.Title>
                            Selecione uma opção
                          </BottomSheet.Title>

                          {patrimonies?.map((patrimony) => (
                            <BottomSheet.Option
                              key={patrimony.id}
                              selected={patrimony.id === selectedPatrimony?.id}
                              onClick={() => {
                                setValue('patrimony', patrimony)
                                setValue('account', patrimony.accounts[0])

                                setSearchParams({
                                  patrimonyId: patrimony.id,
                                  accountId: patrimony.accounts[0].id,
                                })
                              }}
                            >
                              <div className={styles.option}>
                                <div className={styles.titleContainer}>
                                  <Text size="md">{patrimony.name}</Text>
                                  {patrimony.accounts.some(
                                    (account) => !account?.paymentMethod,
                                  ) && <div className={styles.bullet} />}
                                </div>

                                <Text size="sm" weight="regular">
                                  {`${patrimony.address.address}, ${patrimony.address.number}`}
                                </Text>
                              </div>
                            </BottomSheet.Option>
                          ))}
                        </BottomSheet.Content>
                      </BottomSheet.Root>

                      <BottomSheet.Root>
                        <BottomSheet.Trigger disabled={onlyOneAccount}>
                          <div className={styles.selected}>
                            <div>
                              <Text
                                size="sm"
                                weight="medium"
                                color="element-medium"
                                as="label"
                                {...register('account')}
                              >
                                Conta
                              </Text>
                              <Text
                                size="md"
                                weight="regular"
                                color={
                                  onlyOneAccount
                                    ? 'element-disabled'
                                    : 'element-default'
                                }
                              >
                                {selectedAccount
                                  ? `${selectedAccount?.code} - ${selectedAccount?.name}`
                                  : ''}
                              </Text>
                            </div>

                            <IconButton>
                              <Icon
                                width={17}
                                name="edit"
                                color={
                                  onlyOneAccount
                                    ? 'element-disabled'
                                    : 'element-default'
                                }
                              />
                            </IconButton>
                          </div>
                        </BottomSheet.Trigger>

                        <BottomSheet.Content>
                          <BottomSheet.Title>
                            Selecione uma opção
                          </BottomSheet.Title>

                          {selectedPatrimony?.accounts.map((account) => (
                            <BottomSheet.Option
                              key={account.id}
                              selected={account.id === selectedAccount?.id}
                              onClick={() => {
                                setValue('account', account)

                                setSearchParams({
                                  ...Object.fromEntries([...searchParams]),
                                  accountId: account.id,
                                })
                              }}
                            >
                              <div className={styles.titleContainer}>
                                {`${account.code} - ${account.name}`}

                                {!account?.paymentMethod && (
                                  <div className={styles.bullet} />
                                )}
                              </div>
                            </BottomSheet.Option>
                          ))}
                        </BottomSheet.Content>
                      </BottomSheet.Root>
                    </div>
                  </div>

                  <Instruction content="Cada conta pode ter somente um método de pagamento vinculado." />

                  <div>
                    <Text size="lg">Forma de pagamento</Text>

                    {selectedAccount?.paymentMethod ? (
                      <RegisteredPayment
                        accountId={selectedAccount?.id}
                        paymentMethod={selectedAccount?.paymentMethod}
                      />
                    ) : (
                      <EmptyState accountId={selectedAccount?.id} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ),

          error: (
            <div className={styles.error}>
              <Icon
                width={44}
                height={62}
                name="phone"
                color="element-default"
              />
              <Text size="lg" weight="semibold">
                Erro ao carregar
              </Text>
              <Text size="sm" weight="regular">
                Ocorreu um problema inesperado. Feche o navegador e abra
                novamente para tentar outra vez.
              </Text>
            </div>
          ),
          loading: <Loader />,
        }[handleRender()]
      }
    </>
  )
}

export default Method
